import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const USER_TYPE = {
  MEMBER: 0,
  ADMIN: 1
};

export { USER_TYPE };

const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json'
});

const handleError = (error) => {
  console.error('Client API Error:', error);
  if (error.response?.status === 503) {
    return { success: false, error: 'Service temporarily unavailable' };
  }
  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

export const createClient = async (clientData, token) => {
  try {
    const payload = {
      name: clientData.name,
      tenant_id: clientData.tenantId,
      abbr: clientData.abbreviation
    };

    if (clientData.title) payload.title = clientData.title;
    if (clientData.description) payload.description = clientData.description;
    if (clientData.logoUrl) payload.logo_url = clientData.logoUrl;
    if (clientData.faviconUrl) payload.favicon_url = clientData.faviconUrl;

    if (clientData.theme) {
      payload.theme = {
        primary_color: clientData.theme.primaryColor || clientData.theme.primary_color,
        secondary_color: clientData.theme.secondaryColor || clientData.theme.secondary_color,
        background_color: clientData.theme.backgroundColor || clientData.theme.background_color
      };
    }

    if (clientData.font) {
      payload.font = { family: clientData.font.family };
    }

    const response = await axios.post(
      `${BASE_URL}/user/v1/client`,
      payload,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No client data received');

    return {
      success: response.data.status === 0,
      client: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getClientById = async (clientId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/client/${clientId}`,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No client data received');

    return {
      success: response.data.status === 0,
      client: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllClients = async (params, token) => {
  try {
    const queryParams = new URLSearchParams();

    if (params.pagination) {
      queryParams.append('pagination.page', params.pagination.page || 1);
      queryParams.append('pagination.page_size', params.pagination.pageSize || 10);
    }

    if (params.sorting) {
      queryParams.append('sorting.sort_by', params.sorting.sort_by);
      queryParams.append('sorting.order', params.sorting.order);
    }

    if (params.nameFilter) {
      queryParams.append('name_filter', params.nameFilter);
    }

    const response = await axios.get(
      `${BASE_URL}/user/v1/clients?${queryParams.toString()}`,
      { headers: getHeaders(token) }
    );

    const data = response.data;
    if (!data.data) throw new Error('No client data received');

    return {
      success: data.status === 0,
      clients: data.data,
      pagination: {
        totalCount: data.totalCount,
        pageCount: data.pageCount,
        page: data.page,
        pageSize: data.pageSize
      }
    };
  } catch (error) {
    return handleError(error);
  }
};

export const updateClient = async (clientData, token) => {
  try {
    const payload = {
      id: clientData.id,
      name: clientData.name
    };

    if (clientData.abbreviation) payload.abbr = clientData.abbreviation;
    if (clientData.title) payload.title = clientData.title;
    if (clientData.description) payload.description = clientData.description;
    if (clientData.logoUrl) payload.logo_url = clientData.logoUrl;
    if (clientData.faviconUrl) payload.favicon_url = clientData.faviconUrl;

    if (clientData.theme) {
      payload.theme = {
        primary_color: clientData.theme.primaryColor || clientData.theme.primary_color,
        secondary_color: clientData.theme.secondaryColor || clientData.theme.secondary_color,
        background_color: clientData.theme.backgroundColor || clientData.theme.background_color
      };
    }

    if (clientData.font) {
      payload.font = { family: clientData.font.family };
    }

    const response = await axios.patch(
      `${BASE_URL}/user/v1/client/${clientData.id}`,
      payload,
      { headers: getHeaders(token) }
    );

    if (!response.data.data) throw new Error('No client data received');

    return {
      success: response.data.status === 0,
      client: response.data.data
    };
  } catch (error) {
    return handleError(error);
  }
};

export const deleteClient = async (clientId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/client/${clientId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.success && response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const addUserToClient = async (clientId, userId, userType, token) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/user/v1/client/${clientId}/user/${userId}`,
      { client_id: clientId, user_id: userId, type: userType },
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const removeUserFromClient = async (clientId, userId, token) => {
  try {
    const response = await axios.delete(
      `${BASE_URL}/user/v1/client/${clientId}/user/${userId}`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};

export const getAllClientAdminsCredentials = async (clientId, token) => {
  try {
    const response = await axios.get(
      `${BASE_URL}/user/v1/client/${clientId}/admins`,
      { headers: getHeaders(token) }
    );

    return {
      success: response.data.status === 0,
      admins: response.data.data,
      message: response.data.message
    };
  } catch (error) {
    return handleError(error);
  }
};
